import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../services/api';
import { FiX, FiChevronLeft, FiChevronRight, FiInstagram, FiFacebook, FiYoutube, FiTwitter, FiLinkedin, FiPlus, FiCheck } from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';

const INDUSTRY_OPTIONS = ['Technology', 'Fashion', 'Food & Beverage', 'Healthcare', 'Finance', 'Travel & Hospitality', 'Retail', 'Education', 'Other'];
const TONE_OPTIONS = ['Professional', 'Casual', 'Playful', 'Authoritative', 'Friendly', 'Custom'];
const PERSONALITY_OPTIONS = ['Innovative', 'Trustworthy', 'Bold', 'Authentic', 'Energetic', 'Sophisticated'];
const DEMOGRAPHICS_OPTIONS = ['Young Adults (18-30)', 'Professionals (25-45)', 'Families', 'Female Audience', 'Rich People / Luxury', 'Students'];
const GOAL_OPTIONS = ['Awareness', 'Sales', 'Engagement', 'Community', 'Education'];
const MONTH_OPTIONS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function ClientOnboardingModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '', industry: '', customIndustry: '',
    brand_tone: '', customTone: '', brand_personality: [], customPersonality: '',
    brand_values: '', target_audience: '', audience_demographics: [], customDemographic: '',
    goals: [], customGoal: '', key_messages: '', launch_date: '',
    constraints: [], constraintInput: '', brand_colors: '', visual_style: '',
    content_focus_type: '',  // "product", "space", or "hybrid"
    // Social handles
    social_instagram: '',
    social_facebook: '',
    social_youtube: '',
    social_tiktok: '',
    social_twitter: '',
    social_linkedin: '',
    // Seasonal trends (manual input for Phase 2A)
    peak_months: [],
    low_months: [],
    campaign_lead_time: '',
    seasonal_notes: ''
  });

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  
  const toggleArrayItem = (field, item) => {
    const current = formData[field];
    updateField(field, current.includes(item) ? current.filter(i => i !== item) : [...current, item]);
  };

  // Track recently added items for animation
  const [recentlyAdded, setRecentlyAdded] = useState(null);

  const addToArray = (field, inputField) => {
    const value = formData[inputField].trim();
    if (value && !formData[field].includes(value)) {
      updateField(field, [...formData[field], value]);
      updateField(inputField, '');
      // Show visual feedback
      setRecentlyAdded({ field, value });
      setTimeout(() => setRecentlyAdded(null), 1500);
    }
  };

  const removeFromArray = (field, item) => updateField(field, formData[field].filter(i => i !== item));

  // Helper to get custom items (items not in preset options)
  const getCustomItems = (field, presetOptions) => {
    return formData[field].filter(item => !presetOptions.includes(item));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Build social handles object (only include non-empty values)
      const socialHandles = {};
      if (formData.social_instagram) socialHandles.instagram = formData.social_instagram;
      if (formData.social_facebook) socialHandles.facebook = formData.social_facebook;
      if (formData.social_youtube) socialHandles.youtube = formData.social_youtube;
      if (formData.social_tiktok) socialHandles.tiktok = formData.social_tiktok;
      if (formData.social_twitter) socialHandles.twitter = formData.social_twitter;
      if (formData.social_linkedin) socialHandles.linkedin = formData.social_linkedin;

      // Build seasonal trends object
      const seasonalTrends = {};
      if (formData.peak_months.length > 0) seasonalTrends.peak_months = formData.peak_months;
      if (formData.low_months.length > 0) seasonalTrends.low_months = formData.low_months;
      if (formData.campaign_lead_time) seasonalTrends.campaign_lead_time = parseInt(formData.campaign_lead_time);
      if (formData.seasonal_notes) seasonalTrends.notes = formData.seasonal_notes;

      const newClient = await api.createClient({
        name: formData.name,
        industry: formData.industry === 'Other' ? formData.customIndustry : formData.industry,
        brand_tone: formData.brand_tone === 'Custom' ? formData.customTone : formData.brand_tone,
        brand_personality: formData.brand_personality,
        brand_values: formData.brand_values,
        target_audience: formData.target_audience,
        audience_demographics: formData.audience_demographics,
        goals: formData.goals,
        key_messages: formData.key_messages,
        constraints: formData.constraints,
        brand_colors: formData.brand_colors || null,
        visual_style: formData.visual_style || null,
        launch_date: formData.launch_date || null,
        content_focus_type: formData.content_focus_type || null,  // New: Content focus type for Visual Intelligence
        social_handles: Object.keys(socialHandles).length > 0 ? socialHandles : null,
        seasonal_trends: Object.keys(seasonalTrends).length > 0 ? seasonalTrends : null,
      });
      // Pass the created client to the success callback
      onSuccess(newClient);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.upgrade_required) {
        setError(`🔒 ${detail.message || 'Client limit reached. Upgrade to add more clients.'}`);
      } else {
        const errorMessage = detail?.message || 
          (typeof detail === 'string' ? detail : null) ||
          (Array.isArray(detail) ? detail.map(e => e.msg || e.message).join(', ') : null) ||
          'Failed to create client. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch(step) {
      case 1: return formData.name && formData.industry && (formData.industry !== 'Other' || formData.customIndustry);
      case 2: return (formData.brand_tone && (formData.brand_tone !== 'Custom' || formData.customTone)) && formData.brand_personality.length > 0 && formData.brand_values;
      case 3: return formData.target_audience && formData.audience_demographics.length > 0;
      case 4: return formData.goals.length > 0 && formData.key_messages;
      case 5: return true; // Constraints & visuals - optional
      case 6: return true; // Social handles - optional
      default: return false;
    }
  };

  const totalSteps = 6;

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div 
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card rounded-xl w-full max-w-2xl shadow-2xl border border-border/50 flex flex-col"
        style={{ maxHeight: '85vh' }}
      >
        {/* Header - fixed */}
        <div className="flex-shrink-0 flex items-center justify-between p-5 border-b border-border/50">
          <div>
            <h2 className="text-xl font-bold text-foreground">Set Up Your Brand</h2>
            <p className="text-sm text-muted-foreground mt-1">Step {step} of {totalSteps}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" data-testid="close-modal">
            <FiX size={24} />
          </button>
        </div>

        {/* Progress - fixed */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-border/30">
          <div className="flex space-x-2">
            {Array.from({ length: totalSteps }, (_, i) => i + 1).map(s => (
              <div key={s} className={`flex-1 h-2 rounded-full ${s <= step ? 'bg-primary' : 'bg-secondary'}`} />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <h3 className="text-lg font-semibold text-foreground">Basic Information</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                  placeholder="Enter your brand name"
                  data-testid="brand-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Industry/Category *</label>
                <div className="grid grid-cols-3 gap-3">
                  {INDUSTRY_OPTIONS.map(industry => (
                    <button
                      key={industry}
                      onClick={() => updateField('industry', industry)}
                      className={`px-4 py-2 rounded-lg border-2 transition-colors text-sm ${
                        formData.industry === industry ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'
                      }`}
                    >
                      {industry}
                    </button>
                  ))}
                </div>
                {formData.industry === 'Other' && (
                  <div className="mt-3 space-y-2">
                    {/* Show added custom industry as a tag */}
                    {formData.customIndustry.trim() && (
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span 
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm border border-primary/30 ${recentlyAdded?.field === 'customIndustry' ? 'animate-pulse ring-2 ring-primary/50' : ''}`}
                        >
                          {formData.customIndustry}
                          <button 
                            onClick={() => updateField('customIndustry', '')} 
                            className="hover:bg-primary/20 rounded-full p-0.5"
                          >
                            <FiX size={14} />
                          </button>
                        </span>
                      </div>
                    )}
                    
                    {/* Input and Add button - only show if no custom industry set */}
                    {!formData.customIndustry.trim() && (
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          id="customIndustryInput"
                          className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                          placeholder="Specify your industry"
                          data-testid="custom-industry-input"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              const value = e.target.value.trim();
                              if (value) {
                                updateField('customIndustry', value);
                                setRecentlyAdded({ field: 'customIndustry', value });
                                setTimeout(() => setRecentlyAdded(null), 1500);
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const input = document.getElementById('customIndustryInput');
                            const value = input?.value?.trim();
                            if (value) {
                              updateField('customIndustry', value);
                              setRecentlyAdded({ field: 'customIndustry', value });
                              setTimeout(() => setRecentlyAdded(null), 1500);
                            }
                          }}
                          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all flex items-center gap-2"
                          data-testid="add-custom-industry-btn"
                        >
                          <FiPlus size={16} />
                          Add
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Content Focus Type - New addition for Visual Intelligence */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Content Focus (Optional)</label>
                <p className="text-xs text-muted-foreground mb-3">How do you typically create content? This helps AI optimize for your style.</p>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => updateField('content_focus_type', 'product')}
                    className={`px-4 py-3 rounded-lg border-2 transition-colors text-sm flex flex-col items-center gap-1 ${
                      formData.content_focus_type === 'product' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'
                    }`}
                    data-testid="content-focus-product"
                  >
                    <span className="text-lg">📦</span>
                    <span className="font-medium">Product Photos</span>
                    <span className="text-xs text-muted-foreground">Items, merchandise</span>
                  </button>
                  <button
                    onClick={() => updateField('content_focus_type', 'space')}
                    className={`px-4 py-3 rounded-lg border-2 transition-colors text-sm flex flex-col items-center gap-1 ${
                      formData.content_focus_type === 'space' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'
                    }`}
                    data-testid="content-focus-space"
                  >
                    <span className="text-lg">🏠</span>
                    <span className="font-medium">Space Photos</span>
                    <span className="text-xs text-muted-foreground">Interior, storefront</span>
                  </button>
                  <button
                    onClick={() => updateField('content_focus_type', 'hybrid')}
                    className={`px-4 py-3 rounded-lg border-2 transition-colors text-sm flex flex-col items-center gap-1 ${
                      formData.content_focus_type === 'hybrid' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'
                    }`}
                    data-testid="content-focus-hybrid"
                  >
                    <span className="text-lg">🔄</span>
                    <span className="font-medium">Both</span>
                    <span className="text-xs text-muted-foreground">Mix of product & space</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Brand Identity</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand Tone/Voice *</label>
                <div className="grid grid-cols-2 gap-3">
                  {TONE_OPTIONS.map(tone => (
                    <button key={tone} onClick={() => updateField('brand_tone', tone)}
                      className={`px-4 py-2 rounded-lg border-2 ${formData.brand_tone === tone ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'}`}>
                      {tone}
                    </button>
                  ))}
                </div>
                {formData.brand_tone === 'Custom' && (
                  <div className="mt-3 space-y-2">
                    <input type="text" value={formData.customTone} onChange={(e) => updateField('customTone', e.target.value)}
                      className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground" 
                      placeholder="Describe your brand's custom tone (e.g., Minimalist and Calm)" />
                    {formData.customTone && (
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <FiCheck size={16} />
                        <span>Custom tone saved: <strong>{formData.customTone}</strong></span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand Personality * (Select multiple)</label>
                <div className="grid grid-cols-3 gap-2">
                  {PERSONALITY_OPTIONS.map(personality => (
                    <button key={personality} onClick={() => toggleArrayItem('brand_personality', personality)}
                      className={`px-3 py-2 rounded-lg border-2 text-sm transition-all ${formData.brand_personality.includes(personality) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'}`}>
                      {personality}
                    </button>
                  ))}
                </div>
                
                {/* Custom items display */}
                {getCustomItems('brand_personality', PERSONALITY_OPTIONS).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {getCustomItems('brand_personality', PERSONALITY_OPTIONS).map(item => (
                      <span 
                        key={item} 
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm border border-primary/30 ${recentlyAdded?.field === 'brand_personality' && recentlyAdded?.value === item ? 'animate-pulse ring-2 ring-primary/50' : ''}`}
                      >
                        {item}
                        <button onClick={() => removeFromArray('brand_personality', item)} className="hover:bg-primary/20 rounded-full p-0.5">
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="mt-3 flex space-x-2">
                  <input type="text" value={formData.customPersonality} onChange={(e) => updateField('customPersonality', e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('brand_personality', 'customPersonality')}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="Add custom personality trait" />
                  <button 
                    onClick={() => addToArray('brand_personality', 'customPersonality')} 
                    disabled={!formData.customPersonality.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    <FiPlus size={16} />
                    Add
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand Values *</label>
                <textarea value={formData.brand_values} onChange={(e) => updateField('brand_values', e.target.value)} rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg focus:ring-2 focus:ring-primary text-foreground placeholder:text-muted-foreground"
                  placeholder="e.g., Innovation, Sustainability, Customer-first approach" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Target Audience</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Target Audience Description *</label>
                <textarea value={formData.target_audience} onChange={(e) => updateField('target_audience', e.target.value)} rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="Describe who you're targeting" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Demographics * (Select all that apply)</label>
                <div className="grid grid-cols-2 gap-3">
                  {DEMOGRAPHICS_OPTIONS.map(demo => (
                    <button key={demo} onClick={() => toggleArrayItem('audience_demographics', demo)}
                      className={`px-4 py-2 rounded-lg border-2 text-sm transition-all ${formData.audience_demographics.includes(demo) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'}`}>
                      {demo}
                    </button>
                  ))}
                </div>
                
                {/* Custom items display */}
                {getCustomItems('audience_demographics', DEMOGRAPHICS_OPTIONS).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {getCustomItems('audience_demographics', DEMOGRAPHICS_OPTIONS).map(item => (
                      <span 
                        key={item} 
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm border border-primary/30 ${recentlyAdded?.field === 'audience_demographics' && recentlyAdded?.value === item ? 'animate-pulse ring-2 ring-primary/50' : ''}`}
                      >
                        {item}
                        <button onClick={() => removeFromArray('audience_demographics', item)} className="hover:bg-primary/20 rounded-full p-0.5">
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="mt-3 flex space-x-2">
                  <input type="text" value={formData.customDemographic} onChange={(e) => updateField('customDemographic', e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('audience_demographics', 'customDemographic')}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="Add custom demographic" />
                  <button 
                    onClick={() => addToArray('audience_demographics', 'customDemographic')} 
                    disabled={!formData.customDemographic.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    <FiPlus size={16} />
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Strategy & Goals</h3>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Primary Goals *</label>
                <div className="grid grid-cols-3 gap-3">
                  {GOAL_OPTIONS.map(goal => (
                    <button key={goal} onClick={() => toggleArrayItem('goals', goal)}
                      className={`px-4 py-2 rounded-lg border-2 transition-all ${formData.goals.includes(goal) ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:border-border'}`}>
                      {goal}
                    </button>
                  ))}
                </div>
                
                {/* Custom items display */}
                {getCustomItems('goals', GOAL_OPTIONS).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {getCustomItems('goals', GOAL_OPTIONS).map(item => (
                      <span 
                        key={item} 
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm border border-primary/30 ${recentlyAdded?.field === 'goals' && recentlyAdded?.value === item ? 'animate-pulse ring-2 ring-primary/50' : ''}`}
                      >
                        {item}
                        <button onClick={() => removeFromArray('goals', item)} className="hover:bg-primary/20 rounded-full p-0.5">
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="mt-3 flex space-x-2">
                  <input type="text" value={formData.customGoal} onChange={(e) => updateField('customGoal', e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('goals', 'customGoal')}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="Add custom goal" />
                  <button 
                    onClick={() => addToArray('goals', 'customGoal')} 
                    disabled={!formData.customGoal.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    <FiPlus size={16} />
                    Add
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Key Messages/USPs *</label>
                <textarea value={formData.key_messages} onChange={(e) => updateField('key_messages', e.target.value)} rows={3}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="What makes this brand different?" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Launch Date (Optional)</label>
                <input type="date" value={formData.launch_date} onChange={(e) => updateField('launch_date', e.target.value)}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground" />
                <p className="text-xs text-muted-foreground mt-1">Used for campaign timing suggestions</p>
              </div>
            </div>
          )}

          {step === 5 && (
            <div className="space-y-6">
              <h3 className="text-lg font-semibold">Constraints & Visuals</h3>
              <p className="text-sm text-muted-foreground">All fields optional</p>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">What should we avoid in content?</label>
                <p className="text-xs text-muted-foreground mb-3">Add any specific constraints or things to avoid when creating content for this brand.</p>
                
                {/* Constraints display as tags */}
                {formData.constraints.length > 0 && (
                  <div className="mb-3 flex flex-wrap gap-2">
                    {formData.constraints.map((constraint, idx) => (
                      <span 
                        key={idx} 
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 text-red-400 rounded-full text-sm border border-red-500/30 ${recentlyAdded?.field === 'constraints' && recentlyAdded?.value === constraint ? 'animate-pulse ring-2 ring-red-500/50' : ''}`}
                      >
                        {constraint}
                        <button onClick={() => removeFromArray('constraints', constraint)} className="hover:bg-red-500/20 rounded-full p-0.5">
                          <FiX size={14} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex space-x-2">
                  <input type="text" value={formData.constraintInput} onChange={(e) => updateField('constraintInput', e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('constraints', 'constraintInput')}
                    className="flex-1 px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="Add a constraint" />
                  <button 
                    onClick={() => addToArray('constraints', 'constraintInput')} 
                    disabled={!formData.constraintInput.trim()}
                    className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    <FiPlus size={16} />
                    Add
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Brand Colors (Optional)</label>
                <input type="text" value={formData.brand_colors} onChange={(e) => updateField('brand_colors', e.target.value)}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="e.g., Blue (#0066CC), White, Gold" />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Visual Style (Optional)</label>
                <input type="text" value={formData.visual_style} onChange={(e) => updateField('visual_style', e.target.value)}
                  className="w-full px-4 py-2 bg-secondary border border-border rounded-lg text-foreground placeholder:text-muted-foreground" placeholder="e.g., Minimalist, Bold and colorful" />
              </div>
            </div>
          )}

          {step === 6 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold">Social Media & Timing Intelligence</h3>
                <p className="text-sm text-muted-foreground mt-1">Help us understand your client&apos;s online presence and peak seasons</p>
              </div>

              {/* Social Handles Section */}
              <div className="bg-secondary rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Social Media Handles (Optional)</h4>
                <p className="text-xs text-muted-foreground mb-4">Link existing social profiles to help with future analytics integration</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3">
                    <FiInstagram className="text-pink-600" size={20} />
                    <input
                      type="text"
                      value={formData.social_instagram}
                      onChange={(e) => updateField('social_instagram', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="@username"
                      data-testid="social-instagram"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiFacebook className="text-primary" size={20} />
                    <input
                      type="text"
                      value={formData.social_facebook}
                      onChange={(e) => updateField('social_facebook', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="Page name or URL"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiYoutube className="text-red-600" size={20} />
                    <input
                      type="text"
                      value={formData.social_youtube}
                      onChange={(e) => updateField('social_youtube', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="Channel name or URL"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <SiTiktok className="text-foreground" size={18} />
                    <input
                      type="text"
                      value={formData.social_tiktok}
                      onChange={(e) => updateField('social_tiktok', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="@username"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiTwitter className="text-sky-500" size={20} />
                    <input
                      type="text"
                      value={formData.social_twitter}
                      onChange={(e) => updateField('social_twitter', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="@username"
                    />
                  </div>
                  <div className="flex items-center space-x-3">
                    <FiLinkedin className="text-primary" size={20} />
                    <input
                      type="text"
                      value={formData.social_linkedin}
                      onChange={(e) => updateField('social_linkedin', e.target.value)}
                      className="flex-1 px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="Company page URL"
                    />
                  </div>
                </div>
              </div>

              {/* Seasonal Trends Section */}
              <div className="bg-primary/10 rounded-lg p-4">
                <h4 className="font-medium text-foreground mb-3">Seasonal Trends (Optional)</h4>
                <p className="text-xs text-muted-foreground mb-4">Tell us when this client typically sees high and low engagement. This helps us suggest optimal campaign timing.</p>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Peak Months (High engagement/sales)</label>
                    <div className="flex flex-wrap gap-2">
                      {MONTH_OPTIONS.map(month => (
                        <button
                          key={month}
                          onClick={() => toggleArrayItem('peak_months', month)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            formData.peak_months.includes(month)
                              ? 'bg-emerald-600 text-white'
                              : 'bg-card text-muted-foreground hover:bg-emerald-500/10 border border-border'
                          }`}
                        >
                          {month.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Low Months (Slow season)</label>
                    <div className="flex flex-wrap gap-2">
                      {MONTH_OPTIONS.map(month => (
                        <button
                          key={month}
                          onClick={() => toggleArrayItem('low_months', month)}
                          disabled={formData.peak_months.includes(month)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            formData.low_months.includes(month)
                              ? 'bg-orange-500 text-white'
                              : formData.peak_months.includes(month)
                              ? 'bg-secondary text-muted-foreground cursor-not-allowed opacity-50'
                              : 'bg-card text-muted-foreground hover:bg-orange-500/10 border border-border'
                          }`}
                        >
                          {month.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Campaign Lead Time</label>
                    <select
                      value={formData.campaign_lead_time}
                      onChange={(e) => updateField('campaign_lead_time', e.target.value)}
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground"
                    >
                      <option value="">How early should campaigns start?</option>
                      <option value="2">2 weeks before peak</option>
                      <option value="4">1 month before peak</option>
                      <option value="8">2 months before peak</option>
                      <option value="12">3 months before peak</option>
                      <option value="0">Ongoing/No lead time needed</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Additional Notes</label>
                    <textarea
                      value={formData.seasonal_notes}
                      onChange={(e) => updateField('seasonal_notes', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground"
                      placeholder="e.g., Black Friday is huge, Summer weddings drive engagement..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
                <p className="text-sm text-foreground">
                  <strong>Coming Soon:</strong> In a future update, we&apos;ll automatically analyze linked social accounts to detect engagement patterns and suggest optimal campaign timing based on your industry and audience behavior.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer - fixed at bottom */}
        <div className="flex-shrink-0 flex items-center justify-between p-6 border-t border-border bg-card rounded-b-xl">
          <button onClick={() => setStep(s => s - 1)} disabled={step === 1}
            className="flex items-center space-x-2 px-4 py-2 text-foreground hover:text-foreground disabled:opacity-50">
            <FiChevronLeft /> <span>Back</span>
          </button>
          <div className="flex space-x-3">
            {step < totalSteps ? (
              <button onClick={() => setStep(s => s + 1)} disabled={!canProceed()}
                className="flex items-center space-x-2 px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                data-testid="next-step-btn">
                <span>Next</span> <FiChevronRight />
              </button>
            ) : (
              <button onClick={handleSubmit} disabled={loading || !canProceed()}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                data-testid="complete-setup-btn">
                {loading ? 'Creating...' : 'Complete Setup'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

export default ClientOnboardingModal;
